import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppLink } from "../../lib/route-primitives";
import { guestNameSchema, SELF_REGISTER_ALLOWED_PREFIXES } from "@wedding/shared";
import { useTurnstile } from "../../hooks/use-turnstile";
import { useQrRegister } from "../../hooks/use-qr-register";
import { useRegistrationWindow } from "../../hooks/use-registration-window";
import { paths } from "../../lib/paths";
import { PhoneNumberInput } from "../../components/phone";
import { cn } from "../../lib/utils";
import { ArrowRight, Loader2, QrCode } from "lucide-react";
import { PrivacyNote } from "../../components/privacy-note";
import { DemoEnter } from "../../components/demo-enter";
import { useWedding } from "../../contexts/wedding-context";

/**
 * Misafirin QR'dan gelip kaydolduğu TEK form.
 *
 * Görünüm iki varyantla değişir, **giden istek değişmez**: default join
 * sayfası `light`, 20 join teması `theme` (renkler currentColor'dan türer, her
 * tema kendi paletiyle giydirir). Temalar eskiden `DemoFormStub` render
 * ediyordu — hiçbir yere istek atmayan bir taklit form; bu bileşen onun yerine
 * geçer ve `formSlot` ile enjekte edilir (bkz. layouts/types.ts).
 */

export type JoinFormVariant = "light" | "theme";

interface JoinRegisterFormProps {
  slug: string;
  variant?: JoinFormVariant;
  /**
   * Tema arka plan rengi. Yalnız "yüzey" gereken tek yer için: ülke seçim
   * popover'ı saydam olamaz (arkasındaki metin okunur hale gelirdi), o yüzden
   * temanın kendi zeminini `--join-theme-surface` ile devralır.
   */
  surface?: string;
}

const ALLOWED_PREFIX_LABEL = SELF_REGISTER_ALLOWED_PREFIXES.join(", ");

interface VariantStyles {
  card: string;
  label: string;
  input: string;
  button: string;
  muted: string;
  error: string;
  link: string;
  heading: string;
}

const styles: Record<JoinFormVariant, VariantStyles> = {
  light: {
    card: "rounded-2xl border border-[#8d6b5e]/20 bg-white p-8 shadow-sm",
    label: "block text-sm font-medium text-[#1b1817] mb-1.5",
    input:
      "w-full rounded-lg border border-[#8d6b5e]/20 bg-white px-4 py-3.5 text-base text-[#1b1817] placeholder:text-[#1b1817]/40 focus-visible:border-[#8d6b5e] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8d6b5e]",
    button:
      "mt-2 w-full rounded-lg bg-[#8d6b5e] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#7a5c4f] disabled:cursor-not-allowed disabled:opacity-50",
    muted: "text-[#1b1817]/50",
    error: "text-red-600",
    link: "text-[#1b1817]/50 hover:underline",
    heading: "font-semibold text-[#1b1817]",
  },
  theme: {
    card: "",
    label: "block text-xs uppercase tracking-widest opacity-60 mb-2",
    input:
      "w-full border-b border-current/30 bg-transparent py-3 text-base text-current placeholder:opacity-40 focus:border-current focus:outline-none",
    button:
      "mt-8 w-full cursor-pointer border border-current py-4 text-xs font-medium uppercase tracking-widest transition-colors hover:bg-current hover:text-black disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-current",
    muted: "opacity-50",
    error: "text-red-400",
    link: "opacity-50 hover:opacity-100 underline underline-offset-4",
    heading: "font-serif text-xl font-light",
  },
};

export function JoinRegisterForm({ slug, variant = "light", surface }: JoinRegisterFormProps) {
  const { t } = useTranslation();
  const s = styles[variant];
  const surfaceVar = surface
    ? ({ "--join-theme-surface": surface } as React.CSSProperties)
    : undefined;
  const { open: windowOpen } = useRegistrationWindow();
  const { wedding } = useWedding();
  const { step, loading, error, register, verify, reset } = useQrRegister();
  const { containerRef, token: turnstileToken, error: turnstileError, reset: resetTurnstile } = useTurnstile();

  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [phoneE164, setPhoneE164] = useState("");
  const [phoneValid, setPhoneValid] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [code, setCode] = useState("");

  const nameOk = guestNameSchema.safeParse(name).success;
  // Twilio Verify +90 geo-kilitli → başka prefix'e kod gönderilemiyor. Kullanıcı
  // gönder'e basıp 409 yemesin diye ülke seçer seçmez uyarıyoruz.
  const foreignNumber = phoneE164 !== "" && !SELF_REGISTER_ALLOWED_PREFIXES.some((p) => phoneE164.startsWith(p));
  const canSubmit = !loading && Boolean(turnstileToken) && nameOk && phoneValid && !foreignNumber;

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !turnstileToken) return;
    await register(guestNameSchema.parse(name), phoneE164, turnstileToken, smsConsent);
    resetTurnstile();
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    await verify(code);
  }

  /** Sunucu kodu/mesajı → kullanıcıya gösterilecek metin. */
  function errorText(serverError: string): string {
    if (!serverError) return "";
    if (serverError === "Banned") return t("auth.banned");
    if (serverError === "service_unavailable") return t("auth.otpUnavailable");
    if (serverError === "foreign_number") return t("join.foreignNumber");
    // Zod'un isim mesajları ("Name too short" vb.) — formda zaten eleniyor,
    // bu yalnız sunucu son sözü söylediğinde görünür.
    if (serverError.startsWith("Name")) return t("join.invalidName");
    return serverError;
  }

  const loginLink = (
    <AppLink to={paths.wedding.auth(slug)} className={cn("block w-full text-center text-sm", s.link)}>
      {t("join.goToLogin")}
    </AppLink>
  );

  // ── Canlı demo: telefon formu yerine tek dokunuşla giriş (F7) ─────────
  if (wedding?.config?.demo === true) {
    return <DemoEnter slug={slug} classes={s} style={surfaceVar} />;
  }

  // ── Terminal durumlar ────────────────────────────────────────────────
  if (step === "already_registered") {
    return (
      <div className={cn(s.card, "text-center")}>
        <h3 className={s.heading}>{t("join.alreadyRegistered")}</h3>
        <p className={cn("mt-2 text-sm", s.muted)}>{t("join.alreadyRegisteredHint")}</p>
        <div className="mt-6">{loginLink}</div>
        <button type="button" onClick={reset} className={cn("mt-4 block w-full cursor-pointer text-sm", s.link)}>
          {t("common.back")}
        </button>
      </div>
    );
  }

  if (step === "closed" || !windowOpen) {
    return (
      <div className={cn(s.card, "text-center")}>
        <QrCode className={cn("mx-auto mb-4 h-12 w-12", s.muted)} aria-hidden="true" />
        <h3 className={s.heading}>{t("join.registrationClosed")}</h3>
        <p className={cn("mt-2 text-sm", s.muted)}>{t("join.registrationClosedHint")}</p>
        <div className="mt-6">{loginLink}</div>
      </div>
    );
  }

  if (step === "cap_reached") {
    return (
      <div className={cn(s.card, "text-center")}>
        <QrCode className={cn("mx-auto mb-4 h-12 w-12", s.muted)} aria-hidden="true" />
        <h3 className={s.heading}>{t("join.capReached")}</h3>
        <p className={cn("mt-2 text-sm", s.muted)}>{t("join.capReachedHint")}</p>
        <div className="mt-6">{loginLink}</div>
      </div>
    );
  }

  // Misafir kotası dolu — listedeki misafirler yine giriş yapabilir.
  if (step === "guest_limit") {
    return (
      <div className={cn(s.card, "text-center")} role="status" data-testid="join-guest-limit">
        <QrCode className={cn("mx-auto mb-4 h-12 w-12", s.muted)} aria-hidden="true" />
        <h3 className={s.heading}>{t("join.guestLimitReached")}</h3>
        <p className={cn("mt-2 text-sm", s.muted)}>{t("join.guestLimitReachedHint")}</p>
        <div className="mt-6">{loginLink}</div>
      </div>
    );
  }

  // ── OTP adımı ────────────────────────────────────────────────────────
  if (step === "otp") {
    return (
      <form onSubmit={handleVerify} className={s.card}>
        <h3 className={s.heading}>{t("auth.verifyTitle")}</h3>
        <p className={cn("mb-6 mt-2 text-sm", s.muted)}>{t("join.successOtp")}</p>
        <label htmlFor="join-code" className={s.label}>{t("auth.codeLabel")}</label>
        <input
          id="join-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={t("auth.codePlaceholder")}
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          required
          autoFocus
          className={cn(s.input, "text-center text-2xl tracking-[0.5em]")}
        />
        {error && <p role="alert" className={cn("mt-3 text-sm", s.error)}>{errorText(error)}</p>}
        <button type="submit" disabled={loading || code.length < 6} className={cn(s.button, "flex items-center justify-center gap-2")}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          {loading ? t("auth.verifying") : t("auth.verify")}
        </button>
        <button type="button" onClick={reset} className={cn("mt-4 w-full cursor-pointer text-sm", s.link)}>
          {t("auth.changeNumber")}
        </button>
      </form>
    );
  }

  // ── Kayıt formu ──────────────────────────────────────────────────────
  return (
    <form onSubmit={handleRegister} className={s.card} style={surfaceVar}>
      <div className="space-y-5">
        <div>
          <label htmlFor="name" className={s.label}>{t("join.nameLabel")}</label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            placeholder={t("join.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            required
            maxLength={80}
            aria-invalid={nameTouched && name !== "" && !nameOk}
            className={s.input}
          />
          {nameTouched && name !== "" && !nameOk && (
            <p role="alert" className={cn("mt-1.5 text-xs", s.error)}>{t("join.invalidName")}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className={s.label}>{t("auth.phoneLabel")}</label>
          <PhoneNumberInput
            id="phone"
            variant={variant === "theme" ? "theme" : "light"}
            valueE164={phoneE164}
            onChange={(e164, meta) => {
              setPhoneE164(e164);
              setPhoneValid(meta.isValid);
            }}
          />
          {foreignNumber ? (
            <p role="alert" className={cn("mt-2 text-xs leading-relaxed", s.error)}>
              {t("join.foreignNumber")} <span className={s.muted}>{t("join.foreignNumberHint", { prefixes: ALLOWED_PREFIX_LABEL })}</span>
            </p>
          ) : (
            <p className={cn("mt-2 text-xs", s.muted)}>ℹ️ {t("registration.incentiveTextPlaceholder")}</p>
          )}
        </div>

        {/* KVKK: davet/hatırlatma SMS'i için açık rıza — varsayılan İŞARETSİZ */}
        <label className="flex cursor-pointer select-none items-start gap-2.5">
          <input
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-[#8d6b5e]"
          />
          <span className={cn("text-xs leading-relaxed", s.muted)}>{t("join.smsConsentLabel")}</span>
        </label>
        <PrivacyNote className={cn("-mt-1 text-xs leading-relaxed", s.muted)} />

        {error && <p role="alert" className={cn("text-sm", s.error)}>{errorText(error)}</p>}

        <div ref={containerRef} className="flex justify-center" />
        {turnstileError && <p role="alert" className={cn("text-sm", s.error)}>{turnstileError}</p>}

        <button type="submit" disabled={!canSubmit} className={cn(s.button, "flex items-center justify-center gap-2")}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          {loading ? t("join.submitting") : t("join.submit")}
        </button>

        {loginLink}
      </div>
    </form>
  );
}
